import { CancelToken } from 'axios';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../store';
import { getVisitationList, selectVisitationList, visitationListActions } from '../../store/entities/visitationList';
import { selectIdentity } from '../../store/entities/identity';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { Grid } from '../common/grid/Grid';
import { SectionHeader } from '../common/SectionHeader';
import { CellDate } from '../common/grid/CellDate';
import { VisitationFilter } from './VisitationFilter';
import { VisitationAddModal } from './VisitationAddModal';
import { hasRole, isExternal } from '../../utils/roles';
import { getLangNameObj, joinStrings2 } from '../../utils/format';
import { TableRowButton } from '../common/basic/TableRowButton';
import { useTranslation } from 'react-i18next';
import { DateFormat } from '../../utils/date';
import { parseISO } from 'date-fns';
import { InnerTooltipWrapper, QcsTooltip } from '../common/basic/QcsTooltip';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { VisitationDto } from '@api/models/visitations/VisitationDto';
import { InvitationType } from '@api/models/enums/InvitationType';
import { UserRole } from '@api/models/enums/UserRole';
import { Tooltip } from '@mui/material';
import { FaIcon } from '../common/FaIcon';
import { faPersonWalking } from '@fortawesome/free-solid-svg-icons';
import { visitationService } from '@api/services/services';
import { invitationTypeVisitListActions } from '../../store/entities/invitationTypeVisitList';

export const Visitation: FC = () => {
  const { t, i18n } = useTranslation();
  const identity = useAppSelector(selectIdentity);
  const visitationList = useAppSelector(selectVisitationList);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const businessSettings = useBusinessSettings();

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getVisitationList(cancelToken));
  };
  const handleRefresh = (cancelToken: CancelToken) => {
    dispatch(visitationListActions.reload(true));
    handleGetData(cancelToken);
  };

  const handleRenderData = (item: VisitationDto) => {
    return (
      <>
        <QcsTableCell hideOnMobile>
          {(item.invitation?.invitationType === InvitationType.OneTime || item.invitation?.invitationType === InvitationType.OneTimeEntry) && (
            <Tooltip title={t('invitation.visitInvitation')}>
              <span>
                <FaIcon icon={faPersonWalking} style={{ marginRight: '0.5rem' }} />
              </span>
            </Tooltip>
          )}
          {getLangNameObj(i18n, item.invitation)}
        </QcsTableCell>
        <QcsTableCell>{getLangNameObj(i18n, item.visitor)}</QcsTableCell>

        <QcsTableCell>{getLangNameObj(i18n, item.supplier)}</QcsTableCell>
        <QcsTableCell>{item.establishment?.name}</QcsTableCell>
        <QcsTableCell hideOnMobile>{joinStrings2(item.workplaces?.map((x) => getLangNameObj(i18n, x)))}</QcsTableCell>
        <QcsTableCell hideOnMobile>{getLangNameObj(i18n, item.invitedBy)}</QcsTableCell>
        <QcsTableCell hideOnMobile>
          {item.note && item.note?.length > 10 ? (
            <QcsTooltip title={item.note} placement="top">
              <InnerTooltipWrapper>{item.note.substring(0, 10) + '...'}</InnerTooltipWrapper>
            </QcsTooltip>
          ) : (
            item.note
          )}
        </QcsTableCell>
        <QcsTableCell hideOnMobile>{item.carSpz}</QcsTableCell>
        <CellDate value={item.entryTime} showWithTime hideOnMobile />
        <QcsTableCell>
          {item.exitTime && <DateFormat value={parseISO(item.exitTime)} showWithTime />}
          {!item.exitTime && hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist]) && (
            <TableRowButton onClick={handleExit(item)} variant="contained">
              {t('visitation.exit')}
            </TableRowButton>
          )}
        </QcsTableCell>
      </>
    );
  };

  const handleRowClick = (item: VisitationDto) => {
    if (isExternal(identity)) return;
    navigate('/visitation/' + item.id);
  };

  const handleAddClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleExit = (item: VisitationDto) => async () => {
    await visitationService.endVisitation(item.id!).then(() => {
      dispatch(visitationListActions.reload(true));
      dispatch(invitationTypeVisitListActions.reload(true));
    });
  };

  return (
    <>
      <VisitationAddModal key={String(isModalOpen)} open={isModalOpen} onClose={handleModalClose} />

      <SectionHeader
        onRefresh={handleRefresh}
        title="visitation.title"
        addText="visitation.add"
        handleAddClick={
          hasRole(identity.roles, [UserRole.AdminQcs, UserRole.ManagerOsah, UserRole.ManagerWorkplace, UserRole.Receptionist])
            ? handleAddClick
            : undefined
        }
        children={
          businessSettings.turnstilesEnabled && (
            <QcsLoadingButton variant="contained" onClick={() => navigate('/access-point-logs')}>
              {t('visitation.accessPointLog.title')}
            </QcsLoadingButton>
          )
        }
      />
      <Grid<VisitationDto>
        headers={[
          { captionStr: 'visitation.invitation', orderName: 'invitation.name', hideOnMobile: true },
          {
            captionStr: 'visitation.invitationPerson',
          },

          { captionStr: 'visitation.supplier', orderName: 'supplier' },
          {
            captionStr: 'visitation.establishment',
            orderName: 'establishment',
          },
          {
            captionStr: 'visitation.workplace',
            hideOnMobile: true,
          },
          {
            captionStr: 'visitation.invitedBy',
            hideOnMobile: true,
            orderName: 'invitation.employee.name',
          },
          {
            captionStr: 'common.notes',
            hideOnMobile: true,
          },
          {
            captionStr: 'supplier.car.spz',
            hideOnMobile: true,
          },
          {
            captionStr: 'visitation.entryTime',
            hideOnMobile: true,
            orderName: 'entryTime',
          },
          {
            captionStr: 'visitation.exitTime',
            orderName: 'exitTime',
          },
        ]}
        data={visitationList}
        gridActions={visitationListActions}
        renderData={handleRenderData}
        onRowClick={handleRowClick}
        getData={handleGetData}
        filter={<VisitationFilter query={visitationList.query} />}
      />
    </>
  );
};
